import { compact } from "lodash";
import { useCallback, useMemo } from "react";
import {
  EngagementSurveyReportGroupBy,
  EngagementSurveyReportResponderFragment,
  EngagementSurveyRollupType,
} from "types/graphql-schema";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import FormFields from "@components/form/form-fields";
import Input from "@components/input/input";

import ViewOptionsSidebar from "../sidebar/view-options-sidebar";
import EngagementSurveyGroupBySelector from "./engagement-survey-group-by-selector";
import EngagementSurveyReportFilterContainer from "./engagement-survey-report-filter-container";
import { getResponderKey, getResponderLabel } from "./helpers";
import {
  EngagementSurveyReportViewOptions,
  EngagmentSurveyGroupBySelectorItem,
} from "./types";

const EngagementSurveyReportViewOptionsSidebar = ({
  viewOptions,
  onUpdateViewOptions,
  onClose,
  responders,
  isLoading,
  onResetViewToBaseline,
  onSaveView,
  onSaveAsNewView,
  onDeleteView,
  onDuplicateView,
}: {
  viewOptions: EngagementSurveyReportViewOptions;
  responders: EngagementSurveyReportResponderFragment[];
  isLoading: boolean;
  onUpdateViewOptions: (
    opts: Partial<EngagementSurveyReportViewOptions>,
  ) => void;
  onClose: () => void;
  onResetViewToBaseline: () => void;
  onSaveView: () => void;
  onSaveAsNewView: () => void;
  onDuplicateView: () => void;
  onDeleteView: () => void;
}) => {
  const additionalActions = useMemo(() => {
    return (
      <>
        {viewOptions.isDirty && (
          <>
            <Button
              size={ButtonSize.large}
              theme={buttonTheme.text}
              text="Reset"
              onClick={onResetViewToBaseline}
            />
            <Button
              size={ButtonSize.large}
              theme={buttonTheme.primary}
              className="flex items-center gap-1"
              onClick={onSaveView}
              text="Save View"
            />
          </>
        )}
        {viewOptions.id && (
          <Dropdown
            options={compact([
              {
                label: "Duplicate this view",
                onClick: onDuplicateView,
              },
              viewOptions.isDirty && {
                label: "Save as new view",
                onClick: onSaveAsNewView,
              },
              {
                label: "Delete this view",
                onClick: onDeleteView,
              },
            ])}
          />
        )}
      </>
    );
  }, [
    onDeleteView,
    onDuplicateView,
    onResetViewToBaseline,
    onSaveAsNewView,
    onSaveView,
    viewOptions,
  ]);

  const columnResponders = useMemo(
    () =>
      responders.filter(
        (r) =>
          ![
            EngagementSurveyRollupType.Organization,
            EngagementSurveyRollupType.Collapsed,
          ].includes(r.rollupType),
      ),
    [responders],
  );

  const handleChangeGroupBy = useCallback(
    (groupBy: EngagementSurveyReportGroupBy | null) => {
      onUpdateViewOptions({
        groupBy,
        hiddenColumns: [],
      });
    },
    [onUpdateViewOptions],
  );

  const handleChangeHiddenItems = useCallback(
    (newHiddenColumns: string[]) => {
      onUpdateViewOptions({ hiddenColumns: newHiddenColumns });
    },
    [onUpdateViewOptions],
  );

  const selectableGroupNames: EngagmentSurveyGroupBySelectorItem[] =
    useMemo(() => {
      return columnResponders.map((responder) => ({
        key: getResponderKey(responder),
        label: getResponderLabel(responder),
      }));
    }, [columnResponders]);

  return (
    <ViewOptionsSidebar onClose={onClose} additionalActions={additionalActions}>
      <div className="px-6 pb-6">
        <FormFields
          width={120}
          fields={[
            {
              title: "View Name",
              render: () => (
                <Input
                  placeholder="Enter a name..."
                  maxLength={512}
                  value={viewOptions.name}
                  onChange={(e) =>
                    onUpdateViewOptions({
                      name: e.target.value,
                    })
                  }
                />
              ),
            },
          ]}
        />
      </div>
      <div className="border-t p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-semibold">Filters</div>
        </div>
        <div>
          <EngagementSurveyReportFilterContainer
            filters={viewOptions.filters.list}
            onUpdateFilters={(newFilters) =>
              onUpdateViewOptions({
                filters: { ...viewOptions.filters, list: newFilters },
              })
            }
          />
        </div>
      </div>

      <EngagementSurveyGroupBySelector
        groupBy={viewOptions.groupBy}
        onChangeGroupBy={handleChangeGroupBy}
        title="Group By"
        includeNoneOption={false}
        selectableItems={selectableGroupNames}
        hiddenItemKeys={viewOptions.hiddenColumns}
        onChangeHiddenItems={handleChangeHiddenItems}
        infoMessage="Columns that are not selected will be automatically grouped into
                one shared column."
        isLoading={isLoading}
      />
    </ViewOptionsSidebar>
  );
};

export default EngagementSurveyReportViewOptionsSidebar;
